import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  OverlayTrigger,
  Popover,
  Row,
  Spinner
} from 'react-bootstrap';
import CircularPieCharts from '../components/CircularPieCharts';
import AuditsRenderers from '../components/AuditsRenderers';
import MarkdownRenderer from '../components/audit-renderers/MarkdownRenderer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import isoDateToDate from '@/helpers/isoDateToDate';
import URLRendererWithLink from '@/app/common/components/CellRenderers/URLRendererWithLink';
import ChildrensTooltip from '@/components/common/ChildrensTooltip';
import {
  formatCpuThrottlingDetails,
  formatThrottlingDetails,
  getThrottlingDescription
} from '../components/utils';
import { useGetAnalysisSpeedQuery } from '@/app/PageSpeed/api/analysisApi';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import TextLoading from '@/app/common/components/skeleton/TextLoading';
import Image from 'react-bootstrap/Image';
import NewAnalysisForm from '../components/NewAnalysisForm';
import NewAnalysisFormSkeleton from '../components/NewAnalysisFormSkeleton';
import Lightbox from 'react-18-image-lightbox';
import NoAnalysisData from '../components/NoAnalysisData';
import AnalysisIncompleteState from '../components/AnalysisIncompleteState';
import { isAnalysisFinished } from '../utils/analysisStatus';
import {
  listAvailableSpeedStrategies,
  pickSpeedDetails,
  type SpeedStrategy
} from '../utils/speedReport';
import DataLoadError from '@/components/errors/DataLoadError';
import {
  shouldShowDataLoadError,
  isNetworkApiError
} from '@/app/common/utils/apiErrorUtils';
import { ToolAccessDenied } from '@/app/common/components/ToolAccessGuard';
import useToolAccess from '@/app/common/hooks/useToolAccess';
import { breakpointsMediaQuery, useMediaQuery } from '@/helpers/utils';
import type {
  LighthouseCategory,
  ReportLanguage
} from '@/app/PageSpeed/api/types/analysisApiTypes';
import type { ComponentType } from 'react';
import AnalysisPdfDownloadMenu from '../components/AnalysisPdfDownloadMenu';
import PublicReportSettings from '../components/PublicReportSettings';
import { errorMessage, successMessage } from '@/utils/toast';
import {
  MdCheck,
  MdContentCopy,
  MdOpenInNew
} from 'react-icons/md';
import { useUpdateAnalysisVisibilityMutation } from '../api/analysisApi';

const TypedURLRendererWithLink = URLRendererWithLink as ComponentType<any>;

const isPerformanceCategory = (
  key: string,
  category?: LighthouseCategory
) => category?.id === 'performance' || key === 'performance';

const ScoreRangeLegend = () => (
    <div className="d-flex flex-wrap fs--1 justify-content-center gap-2 gap-md-4 align-items-center">
    <div className="d-flex align-items-center gap-1">
      <FontAwesomeIcon className="text-danger" icon="circle" />
      0-49
    </div>
    <div className="d-flex align-items-center gap-1">
      <FontAwesomeIcon className="text-warning" icon="circle" />
      50-89
    </div>
    <div className="d-flex align-items-center gap-1">
      <FontAwesomeIcon className="text-success" icon="circle" />
      90-100
    </div>
  </div>
);

const getOrderedCategoryEntries = (
  categories: Record<string, LighthouseCategory>
) =>
  (Object.entries(categories) as [string, LighthouseCategory][]).sort(
    ([first], [second]) =>
      first === 'performance' ? -1 : second === 'performance' ? 1 : 0
  );

type SpeedProps = {
  publicReport?: boolean;
};

const Speed = ({ publicReport = false }: SpeedProps) => {
  const isMdUp = useMediaQuery(breakpointsMediaQuery.md);
  const access = useToolAccess('analysis', { skip: publicReport });
  const [reportLanguage, setReportLanguage] = useState<ReportLanguage>('fa');
  const [strategy, setStrategy] = useState<SpeedStrategy>('mobile');

  const navigate = useNavigate();
  const { slug } = useParams<{ slug?: string }>();
  const {
    data: analysisData,
    isFetching: isLoadingDetails,
    isError,
    error,
    refetch,
    isFetching,
    isSuccess
  } = useGetAnalysisSpeedQuery(
    {
      id: publicReport ? undefined : slug || '',
      slug: publicReport ? slug : undefined,
      report_lang: reportLanguage,
      strategy
    },
    {
      skip: publicReport
        ? !slug
        : access.isLoading || !access.haveAccess || !slug
    }
  );

  useEffect(() => {
    if (!publicReport) return;
    if (!slug) {
      navigate('/errors/404', { replace: true });
      return;
    }
    if (isLoadingDetails || isError) return;
    if (isSuccess && analysisData?.data == null) {
      navigate('/errors/404', { replace: true });
    }
  }, [
    publicReport,
    slug,
    isLoadingDetails,
    isError,
    isSuccess,
    analysisData,
    navigate
  ]);

  const location = useLocation();
  // SCROLLING TO # AFTER DATA LOAD!
  useEffect(() => {
    if (location.hash && !isLoadingDetails) {
      const element = document.getElementById(location.hash.substring(1));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location, isLoadingDetails]);
  const chartRef = useRef<unknown>(null);
  const [srcImage, setSrcImage] = useState<string>('');
  const [isPublic, setIsPublic] = useState(false);
  const [copied, setCopied] = useState(false);
  const [updateVisibility, { isLoading: isUpdatingVisibility }] =
    useUpdateAnalysisVisibilityMutation();

  useEffect(() => {
    setIsPublic(Boolean(analysisData?.data?.settings?.is_public));
  }, [analysisData?.data?.settings?.is_public]);

  const strategies = useMemo(
    () => listAvailableSpeedStrategies(analysisData?.data),
    [analysisData?.data]
  );
  const selected = pickSpeedDetails(analysisData?.data, strategy);
  const details = selected.details;
  const capturedAt = details?.fetchTime || selected.created_at;

  const publicSlug = analysisData?.data?.settings?.slug;
  const publicUrl = publicSlug
    ? `${import.meta.env.VITE_REACT_APP_MIZFA_TOOLS_PANEL_URL}/reporting/page_speed/${publicSlug}`
    : '';

  const handleVisibilityChange = async (nextIsPublic: boolean) => {
    const taskId = analysisData?.data?.task_id || slug;
    if (!taskId) return;

    try {
      await updateVisibility({
        task_id: taskId,
        is_public: nextIsPublic
      }).unwrap();
      setIsPublic(nextIsPublic);
      await refetch();
      successMessage(nextIsPublic ? 'گزارش عمومی شد.' : 'گزارش خصوصی شد.');
    } catch {
      errorMessage('تغییر وضعیت گزارش ناموفق بود.');
    }
  };

  const handleCopyPublicUrl = async () => {
    if (!publicUrl) return;

    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      successMessage('لینک عمومی کپی شد.');
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      errorMessage('کپی لینک عمومی ناموفق بود.');
    }
  };

  const handleImageClick = (event: MouseEvent<HTMLImageElement>) => {
    event.preventDefault();
    setSrcImage(event.currentTarget.getAttribute('src') || '');
  };

  if (!publicReport && access.isLoading) return <></>;
  if (!publicReport && !access.haveAccess) {
    return <ToolAccessDenied access={access} tool_label="ابزار واکاوی" />;
  }

  if (shouldShowDataLoadError(isError, error)) {
    return (
      <DataLoadError
        onRetry={() => refetch()}
        isRetrying={isFetching}
        isNetworkError={isNetworkApiError(error)}
      />
    );
  }
  if (publicReport && !isLoadingDetails && !analysisData?.data) {
    return null;
  }
  if (!isLoadingDetails && !analysisData?.data) return <NoAnalysisData />;
  if (!isLoadingDetails && !isAnalysisFinished(analysisData?.data?.status)) {
    return (
      <AnalysisIncompleteState
        status={analysisData?.data?.status}
        taskId={analysisData?.data?.task_id}
        onRefresh={() => refetch()}
      />
    );
  }

  return (
    <>
      {srcImage && (
        <Lightbox
          mainSrc={srcImage}
          onCloseRequest={() => setSrcImage('')}
          reactModalStyle={{ overlay: { zIndex: 999999 } }}
          onImageLoad={() => {
            window.dispatchEvent(new Event('resize'));
          }}
        />
      )}
      {!publicReport &&
        (!isLoadingDetails ? (
          <NewAnalysisForm initialSettings={analysisData?.data?.settings} />
        ) : (
          <NewAnalysisFormSkeleton />
        ))}
      {!publicReport && (
        <div
          className="d-flex flex-wrap justify-content-end align-items-center gap-2 my-3"
          style={{ direction: 'ltr', maxWidth: '100%' }}
        >
        {slug && (
          <div style={{ order: 1 }}>
            <AnalysisPdfDownloadMenu
              taskId={slug}
              reportLanguage={reportLanguage}
            />
          </div>
        )}
        {isLoadingDetails ? (
          <span
            className="d-inline-flex align-items-center"
            style={{ order: 2 }}
            aria-hidden
          >
            <TextLoading width={31} height={31} isWidthPx />
          </span>
        ) : (
          <OverlayTrigger
            trigger="click"
            placement="bottom-start"
            rootClose
            overlay={
              <Popover
                id="analysis-visibility-popover"
                dir="rtl"
                style={{
                  width: 'min(360px, calc(100vw - 32px))',
                  maxWidth: 'calc(100vw - 32px)'
                }}
              >
                <Popover.Header as="h6" className="mb-0">
                  {isPublic ? 'گزارش عمومی' : 'گزارش خصوصی'}
                </Popover.Header>
                <Popover.Body className="p-3">
                  {isPublic && publicUrl ? (
                    <>
                      <Form.Label className="fs--1 mb-1">
                        لینک عمومی گزارش
                      </Form.Label>
                      <Form.Control
                        className="mb-3 fs--1"
                        dir="ltr"
                        readOnly
                        value={publicUrl}
                        aria-label="لینک عمومی گزارش"
                      />
                      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                        <div className="d-flex flex-wrap align-items-center gap-2">
                          <Button
                            size="sm"
                            variant="falcon-default"
                            onClick={handleCopyPublicUrl}
                          >
                            {copied ? (
                              <MdCheck
                                size={16}
                                style={{ width: 16, height: 16 }}
                              />
                            ) : (
                              <MdContentCopy
                                size={16}
                                style={{ width: 16, height: 16 }}
                              />
                            )}{' '}
                            {copied ? 'کپی شد' : 'کپی'}
                          </Button>
                          <Button
                            size="sm"
                            variant="falcon-default"
                            onClick={() =>
                              window.open(
                                publicUrl,
                                '_blank',
                                'noopener,noreferrer'
                              )
                            }
                          >
                            <MdOpenInNew
                              size={16}
                              style={{ width: 16, height: 16 }}
                            />{' '}
                            رفتن به لینک
                          </Button>
                        </div>
                        <Form.Check
                          type="switch"
                          id="analysis-public-switch"
                          className="mb-0"
                          label="عمومی"
                          checked={isPublic}
                          disabled={isUpdatingVisibility}
                          onChange={event =>
                            handleVisibilityChange(event.target.checked)
                          }
                        />
                      </div>
                    </>
                  ) : (
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="fs--1">این گزارش خصوصی است.</span>
                      <Form.Check
                        type="switch"
                        id="analysis-private-switch"
                        label="عمومی کردن"
                        checked={isPublic}
                        disabled={isUpdatingVisibility}
                        onChange={event =>
                          handleVisibilityChange(event.target.checked)
                        }
                      />
                    </div>
                  )}
                  {isUpdatingVisibility && (
                    <Spinner animation="border" size="sm" className="mt-2" />
                  )}
                </Popover.Body>
              </Popover>
            }
          >
            <Button
              size="sm"
              variant={isPublic ? 'outline-primary' : 'outline-secondary'}
              className="d-inline-flex align-items-center justify-content-center"
              aria-label={isPublic ? 'گزارش عمومی' : 'گزارش خصوصی'}
              title={isPublic ? 'گزارش عمومی' : 'گزارش خصوصی'}
              style={{ order: 2, height: '31px' }}
            >
              <FontAwesomeIcon icon={isPublic ? 'lock-open' : 'lock'} />
            </Button>
          </OverlayTrigger>
        )}
        <div
          className="d-flex flex-wrap align-items-center gap-2"
          dir="rtl"
          style={{ order: 3, marginLeft: 'auto', maxWidth: '100%', minWidth: 0 }}
        >
          {strategies.length > 1 && (
            <Form.Group
              controlId="analysis-report-strategy"
              className="d-flex align-items-center gap-2 w-auto"
            >
              <Form.Label className="mb-0 fs--1">دستگاه</Form.Label>
              <Form.Select
                size="sm"
                className="fs--1"
                style={{ width: '108px', maxWidth: '100%' }}
                value={strategy}
                onChange={event =>
                  setStrategy(event.target.value as SpeedStrategy)
                }
                disabled={isLoadingDetails}
                aria-label="دستگاه"
              >
                {strategies.includes('mobile') && (
                  <option value="mobile">گوشی</option>
                )}
                {strategies.includes('desktop') && (
                  <option value="desktop">رایانه</option>
                )}
              </Form.Select>
            </Form.Group>
          )}
          <Form.Group
            controlId="analysis-report-language"
            className="d-flex align-items-center gap-2 w-auto"
          >
            <Form.Label className="mb-0 fs--1">زبان گزارش</Form.Label>
            <Form.Select
              size="sm"
              className="fs--1"
              style={{ width: '108px', maxWidth: '100%' }}
              value={reportLanguage}
              onChange={event =>
                setReportLanguage(event.target.value as ReportLanguage)
              }
              aria-label="زبان گزارش"
            >
              <option value="fa">فارسی</option>
              <option value="en">English</option>
            </Form.Select>
          </Form.Group>
        </div>
        </div>
      )}
      <div
        dir={reportLanguage === 'fa' ? 'rtl' : 'ltr'}
        id="analysis-details"
        className={`${reportLanguage === 'fa' ? 'report-rtl' : 'report-ltr'} speed-report-layout${
          publicReport ? ' public-speed-report' : ''
        }`}
        style={{
          textAlign: reportLanguage === 'fa' ? 'right' : 'left',
          ...(publicReport
            ? {
                width: '100%',
                maxWidth: '1200px',
                margin: '16px auto',
                padding: '0 16px',
                boxSizing: 'border-box'
              }
            : {})
        }}
      >
        {publicReport && (
          <Card>
            <Card.Body className="d-flex justify-content-between align-items-center flex-wrap gap-3 py-3 px-4">
              <PublicReportSettings
                settings={analysisData?.data?.settings}
                language={reportLanguage}
                isLoading={isLoadingDetails}
              />
              <div
                className="d-flex flex-wrap align-items-center gap-2"
                dir="rtl"
                style={{ maxWidth: '100%', minWidth: 0 }}
              >
                {strategies.length > 1 && (
                  <Form.Group
                    controlId="public-analysis-report-strategy"
                    className="d-flex align-items-center gap-2 w-auto"
                  >
                    <Form.Label className="mb-0 fs--1">دستگاه</Form.Label>
                    <Form.Select
                      size="sm"
                      className="fs--1"
                      style={{ width: '108px', maxWidth: '100%' }}
                      value={strategy}
                      onChange={event =>
                        setStrategy(event.target.value as SpeedStrategy)
                      }
                      disabled={isLoadingDetails}
                      aria-label="دستگاه"
                    >
                      {strategies.includes('mobile') && (
                        <option value="mobile">گوشی</option>
                      )}
                      {strategies.includes('desktop') && (
                        <option value="desktop">رایانه</option>
                      )}
                    </Form.Select>
                  </Form.Group>
                )}
                <Form.Group
                  controlId="public-analysis-report-language"
                  className="d-flex align-items-center gap-2 w-auto"
                >
                  <Form.Label className="mb-0 fs--1">زبان گزارش</Form.Label>
                  <Form.Select
                    size="sm"
                    className="fs--1"
                    style={{ width: '108px', maxWidth: '100%' }}
                    value={reportLanguage}
                    onChange={event =>
                      setReportLanguage(event.target.value as ReportLanguage)
                    }
                    aria-label="زبان گزارش"
                  >
                    <option value="fa">فارسی</option>
                    <option value="en">English</option>
                  </Form.Select>
                </Form.Group>
              </div>
            </Card.Body>
          </Card>
        )}
        <style>
          {/* استایل های مربوط به آکوردیون های آدیت */}
          {`#analysis-details .accordion-item {
       border-radius:0px;
       }
       #analysis-details .accordion {
        margin-bottom:-1px !important;
       }
       #analysis-details .accordion-button::after {
        margin-left:auto !important;
        margin-right:unset !important;
       }
       #analysis-details.report-rtl .accordion-button,
       #analysis-details.report-rtl .accordion-body {
        direction:rtl !important;
        text-align:right !important;
       }
       #analysis-details.report-rtl .accordion-button::after {
        margin-right:auto !important;
        margin-left:unset !important;
       }
       #analysis-details {
        font-feature-settings: "tnum" 0;
        font-variant-numeric: normal;
        unicode-bidi: plaintext;
        font-family: "Arial", "Helvetica", sans-serif; /* Specify fonts with English numerals */
       }
       #analysis-details.speed-report-layout {
        display:flex;
        flex-direction:column;
        gap:16px;
       }
       #analysis-details.speed-report-layout > div[id] {
        display:flex;
        flex-direction:column;
        gap:16px;
       }
       #analysis-details.speed-report-layout #audits {
        display:flex;
        flex-direction:column;
        gap:16px;
       }
       #analysis-details.speed-report-layout .card {
        margin:0 !important;
       }
       #analysis-details.speed-report-layout .my-3,
       #analysis-details.speed-report-layout .mt-6,
       #analysis-details.speed-report-layout .mb-3 {
        margin-top:0 !important;
        margin-bottom:0 !important;
       }
       #analysis-details.speed-report-layout .metrics-screenshot-gallery {
        margin-top:16px !important;
       }
       #analysis-details {
        max-width:100%;
        min-width:0;
       }
       #analysis-details img {
        max-width:100%;
        height:auto;
       }
       #analysis-details .accordion-button {
        white-space:normal;
       }
       #analysis-details a,
       #analysis-details p,
       #analysis-details pre,
       #analysis-details code {
        overflow-wrap:anywhere;
       }
       #analysis-details .ag-theme-custom,
       #analysis-details .ag-root-wrapper {
        max-width:100%;
       }
       #analysis-details .btn-link .d-flex {
        flex-wrap:wrap;
        min-width:0;
       }
       `}
        </style>
        {!isLoadingDetails ? (
          <>
            {analysisData?.data ? (
              <Card className="my-3">
                <Card.Body>
                  <Row className="justify-content-center g-4">
                    <Col
                      xs={12}
                      className="my-2 py-3 d-flex align-items-center justify-content-center text-center"
                    >
                      <div className="w-100" style={{ minWidth: 0 }}>
                        <TypedURLRendererWithLink
                          to={
                            details?.requestedUrl ||
                            analysisData?.data?.settings?.url
                          }
                          url={
                            details?.requestedUrl ||
                            analysisData?.data?.settings?.url
                          }
                        />
                      </div>
                    </Col>
                    {details?.categories &&
                      getOrderedCategoryEntries(
                        details.categories
                      ).map(([title, value]) => (
                        <Col key={title} xs="auto">
                          <a
                            className="d-flex flex-column gap-1 align-items-center"
                            href={`#${value?.id}`}
                          >
                            <CircularPieCharts
                              data={value}
                              chartRef={chartRef}
                            />
                            <div
                              className="w-100 fw-bold fs-0 text-center"
                            >
                              {value?.title}
                            </div>
                          </a>
                        </Col>
                      ))}
                  </Row>
                </Card.Body>
              </Card>
            ) : (
              <></>
            )}
            <style>
              {`
              #audits .accordion-button{
              padding:${!isMdUp ? '10px' : ''};
              }
              `}
            </style>
            {/* این بخش برای اون کارت هاییه که وضعیت کلی رو نشون میدن با نمودار دایره ای */}
            {details?.categories &&
              getOrderedCategoryEntries(
                details.categories
              ).map(([title, value]) => (
                <div id={title} key={title}>
                  <Card className="mt-6 mb-3 p-md-3 p-1 ">
                    <Card.Body>
                      <Row className="align-items-center g-3">
                        {/* کارت عملکرد نسبت به بقیه فرق داره برای همین اینجاست */}
                        {isPerformanceCategory(title, value) ? (
                          <>
                            <Col
                              className="d-flex flex-column px-3 align-items-center justify-content-center"
                              lg={6}
                              md={12}
                            >
                              <CircularPieCharts
                                data={value}
                                chartRef={chartRef}
                              />
                                <div
                                  className="w-100 fw-bold mt-1 fs-1 fs-md-2 text-center"
                                >
                                {value?.title}
                              </div>
                                <div
                                  className={`w-100 fs--1 my-2 px-md-5 px-0 ${
                                    reportLanguage === 'fa'
                                      ? 'text-start'
                                      : 'text-end'
                                  }`}
                                >
                                <MarkdownRenderer
                                  content={value?.description}
                                />
                                <MarkdownRenderer
                                  content={
                                    details?.i18n
                                      ?.rendererFormattedStrings
                                      ?.varianceDisclaimer
                                  }
                                />
                              </div>
                              <ScoreRangeLegend />
                            </Col>
                            <Col
                              className="d-flex flex-column align-items-center justify-content-center"
                              lg={6}
                              md={12}
                            >
                              <Image
                                src={
                                  details?.audits?.[
                                    'final-screenshot'
                                  ]?.details?.data
                                }
                                thumbnail
                                onClick={e => handleImageClick(e)}
                                style={{
                                  width: '100%',
                                  maxWidth: 'min(336px, 100%)',
                                  maxHeight: '269px'
                                }}
                                className="border cursor-pointer"
                              />

                              {/* <CircularPieCharts data={value} chartRef={chartRef} />
                      <div className="fw-bold my-2 fs-0">{value?.title}</div>
                      <div className="text-center my-2 px-5">
                        <MarkdownRenderer content={value?.description} />
                      </div> */}
                            </Col>
                          </>
                        ) : (
                          //  بقیه کارت های دارای نمودار داخلی
                          <>
                            <Col
                              md={12}
                              className="d-flex justify-content-center align-items-center"
                              lg={2}
                            >
                              <CircularPieCharts
                                data={value}
                                chartRef={chartRef}
                              />
                            </Col>

                            <Col md={12} lg={10}>
                                <div
                                  className="fw-bold my-2 px-0 px-md-5 fs-1 fs-md-2 text-center"
                                >
                                {value?.title}
                              </div>
                                <div
                                  className={`fs--1 my-2 px-md-5 px-0 ${
                                    reportLanguage === 'fa'
                                      ? 'text-start'
                                      : 'text-end'
                                  }`}
                                >
                                <MarkdownRenderer
                                  content={value?.description}
                                />
                                <MarkdownRenderer
                                  content={
                                    details?.i18n
                                      ?.rendererFormattedStrings
                                      ?.varianceDisclaimer
                                  }
                                />
                              </div>
                              <ScoreRangeLegend />
                            </Col>
                          </>
                        )}
                      </Row>
                    </Card.Body>
                  </Card>
                  {/* <h2 className="my-3">{title}</h2> */}
                  {details && (
                    <div id="audits">
                      <AuditsRenderers
                        cat={value}
                        handleImageClick={handleImageClick}
                        refs={value?.auditRefs || []}
                        analysisData={details}
                          direction={reportLanguage === 'fa' ? 'rtl' : 'ltr'}
                          language={reportLanguage}
                      />
                    </div>
                  )}
                </div>
              ))}

            {analysisData?.data && (
              <Card className="mt-6 mb-3">
                <Card.Body>
                  <Row className="justify-content-center g-4">
                    <Col className="" xs={12} md={6} lg={4}>
                      Captured at{' '}
                      {capturedAt ? isoDateToDate(capturedAt) : '—'}
                    </Col>
                    <Col
                      className="text-decoration-underline"
                      xs={12}
                      md={6}
                      lg={4}
                    >
                      <ChildrensTooltip
                        html={
                          <>
                            CPU/Memory Power:{' '}
                            {
                              details?.environment
                                ?.benchmarkIndex
                            }
                            <br />
                            CPU throttling:{' '}
                            {formatCpuThrottlingDetails(
                              details?.configSettings
                                ?.throttling,
                              details?.configSettings
                                ?.throttlingMethod
                            )}
                            <br />
                            Screen emulation:{' '}
                            {
                              details?.configSettings
                                ?.emulatedFormFactor
                            }
                          </>
                        }
                      >
                        Emulated with Desktop Lighthouse &nbsp;
                        {details?.lighthouseVersion}
                      </ChildrensTooltip>
                    </Col>
                    <Col
                      className="text-decoration-underline"
                      xs={12}
                      md={6}
                      lg={4}
                    >
                      <ChildrensTooltip>Single page load</ChildrensTooltip>
                    </Col>
                    <Col
                      className="text-decoration-underline"
                      xs={12}
                      md={6}
                      lg={4}
                    >
                      <ChildrensTooltip
                        html={formatThrottlingDetails(
                          details?.configSettings
                            ?.throttling
                        )}
                      >
                        {getThrottlingDescription(
                          details?.configSettings
                            ?.throttlingMethod
                        )}
                      </ChildrensTooltip>
                    </Col>
                    <Col
                      className="text-decoration-underline"
                      xs={12}
                      md={6}
                      lg={4}
                    >
                      <ChildrensTooltip
                        html={details?.userAgent}
                      >
                        Using Chromium
                      </ChildrensTooltip>
                    </Col>
                    <Col
                      className="text-decoration-underline"
                      xs={12}
                      md={6}
                      lg={4}
                    >
                      Initial page load
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}
          </>
        ) : (
          <>
            <Card className="my-3">
              <Card.Body>
                <Row className="justify-content-center g-4">
                  <Col
                    xs={12}
                    className="my-2 py-3 d-flex align-items-center justify-content-center text-center"
                  >
                    <div>
                      <TextLoading height={18} width={150} isWidthPx />
                    </div>
                  </Col>
                  {Array.from({ length: 5 }).map((_, index) => (
                    <Col key={index} xs="auto">
                      <a
                        className="d-flex flex-column gap-1 align-items-center"
                        href={`#`}
                      >
                        <CircularPieCharts
                          data={{
                            // score: Math.random().toFixed(1),
                            score: 0,
                            id: 'loading',
                            name: 'loading...'
                          }}
                          chartRef={chartRef}
                        />
                        <div className="fw-bold mt-1 text-center fs-0">
                          <TextLoading width={80} height={16} isWidthPx />
                        </div>
                      </a>
                    </Col>
                  ))}
                </Row>
              </Card.Body>
            </Card>
            <Card className="mt-6 mb-3 p-3">
              <Card.Body>
                <Row className="align-items-center">
                  <Col
                    className="d-flex flex-column px-3 align-items-center justify-content-center"
                    lg={6}
                    md={12}
                  >
                    <CircularPieCharts
                      data={{
                        // score: Math.random().toFixed(1),
                        score: 0,
                        id: 'loading',
                        name: 'loading...'
                      }}
                      chartRef={chartRef}
                    />
                    <div className="fw-bold mt-1 text-center fs-1 fs-md-2">
                      <TextLoading isWidthPx width={60} height={20} />
                    </div>
                    <div className="text-center my-2 px-5">
                      <TextLoading width={250} height={40} isWidthPx />
                    </div>
                    <ScoreRangeLegend />
                  </Col>
                  <Col
                    className="d-flex flex-column align-items-center justify-content-center"
                    lg={6}
                    md={12}
                  >
                    <TextLoading width={336} isWidthPx height={269} />
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </>
        )}
      </div>
    </>
  );
};

export default Speed;

export const PublicSpeed = () => <Speed publicReport />;
